import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, CheckCircle, Clock, DollarSign } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import {
  LoadingSpinner,
  MetricCard,
  MetricCardsGrid,
  EmptyState,
  Pagination,
  DataTableFilters,
} from '@/shared/components';
import { ordersApi } from './api';
import { OrderTable, OrderDetailModal } from './components';
import { formatCurrency } from '@/lib/utils';
import type { Order, PaginatedOrders, OrderMetrics } from './types';

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debouncedValue;
}

export function OrdersPage() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 5, total: 0, totalPages: 0 });
  const [metrics, setMetrics] = useState<OrderMetrics>({ totalOrders: 0, totalAttributed: 0, totalPending: 0, totalRevenue: 0, attributionRate: 0 });
  const [loading, setLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [_error, setError] = useState<string | null>(null);
  const [attributedFilter, setAttributedFilter] = useState<string>('all');
  const [queryValue, setQueryValue] = useState('');
  const debouncedSearch = useDebouncedValue(queryValue, 300);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  const fetchOrders = useCallback(async (background = false) => {
    if (!background) {
      setLoading(true);
    }
    setError(null);
    try {
      let attributed: boolean | null = null;
      if (attributedFilter === 'true') attributed = true;
      if (attributedFilter === 'false') attributed = false;

      const data: PaginatedOrders = await ordersApi.list({
        page: pagination.page,
        limit: pagination.limit,
        attributed,
        search: debouncedSearch || undefined,
      });
      setOrders(data.data || []);
      setPagination(data.pagination);
      if (data.metrics) {
        setMetrics(data.metrics);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch orders');
    } finally {
      setLoading(false);
      setIsInitialLoad(false);
    }
  }, [pagination.page, pagination.limit, attributedFilter, debouncedSearch]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleViewOrder = (orderId: number) => {
    const order = orders.find((o) => o.id === orderId);
    if (order) {
      setSelectedOrder(order);
      setDetailModalOpen(true);
    }
  };

  const handleViewFullJourney = (orderId: number) => {
    navigate(`/journeys?orderId=${orderId}`);
  };

  const handleAttributedFilterChange = (value: string) => {
    setAttributedFilter(value);
    setPagination((p) => ({ ...p, page: 1 }));
  };

  const handleQueryChange = (value: string) => {
    setQueryValue(value);
  };

  const handleQueryClear = () => {
    setQueryValue('');
    setPagination((p) => ({ ...p, page: 1 }));
  };

  if (isInitialLoad && loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <LoadingSpinner />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <MetricCardsGrid>
        <MetricCard
          title="Total Orders"
          value={metrics.totalOrders}
          icon={ShoppingCart}
          color="default"
        />
        <MetricCard
          title="Attributed"
          value={metrics.totalAttributed}
          icon={CheckCircle}
          color="success"
          subtitle={`${metrics.attributionRate}% rate`}
        />
        <MetricCard
          title="Pending"
          value={metrics.totalPending}
          icon={Clock}
          color="warning"
        />
        <MetricCard
          title="Total Revenue"
          value={formatCurrency(metrics.totalRevenue)}
          icon={DollarSign}
          color="default"
        />
      </MetricCardsGrid>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Attribution Log</CardTitle>
              <CardDescription>
                Track order attribution and distributor assignments
              </CardDescription>
            </div>
            <span className="text-sm text-muted-foreground">{pagination.total} orders</span>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <DataTableFilters
            searchPlaceholder="Search by name, email, or order #..."
            searchValue={queryValue}
            onSearchChange={handleQueryChange}
            onSearchClear={handleQueryClear}
            filters={[
              {
                key: 'attributed',
                label: 'Status',
                options: [
                  { label: 'Attributed', value: 'true' },
                  { label: 'Not Attributed', value: 'false' },
                ],
                value: attributedFilter,
                onChange: handleAttributedFilterChange,
              },
            ]}
            onClearAll={() => {
              setAttributedFilter('all');
              setQueryValue('');
            }}
          />

          {orders.length === 0 ? (
            <EmptyState heading="No orders found">
              <p>Orders will appear here once customers make purchases.</p>
            </EmptyState>
          ) : (
            <>
              <OrderTable orders={orders} onViewJourney={handleViewOrder} loading={loading} />

              <Pagination
                page={pagination.page}
                totalPages={pagination.totalPages || 1}
                onPrevious={() => setPagination((p) => ({ ...p, page: Math.max(1, p.page - 1) }))}
                onNext={() => setPagination((p) => ({ ...p, page: Math.min(p.totalPages || 1, p.page + 1) }))}
                loading={loading}
              />
            </>
          )}
        </CardContent>
      </Card>

      <OrderDetailModal
        order={selectedOrder}
        open={detailModalOpen}
        onOpenChange={setDetailModalOpen}
        onViewJourney={handleViewFullJourney}
      />
    </div>
  );
}
